/**
 * @module engine/smartGuides
 * @summary Pink alignment guides shown while dragging shapes.
 * @description Compares the moving shapes' edges/centres against every other
 *   shape; when an edge is within ~4 world-units of another, a guide line is
 *   recorded in `S.smartGuides` (drawn by the renderer).
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 4220-4245.
 */
import { S } from './state';

type EdgeMap = Record<string, number>;

/** Recompute alignment guides for the currently moving shape ids. */
export function computeSmartGuides(movingIds: string[]): void {
  S.smartGuides = [];
  if (!movingIds || !movingIds.length) return;
  const moving = S.shapes.filter((s) => movingIds.includes(s.id));
  const others = S.shapes.filter((s) => !movingIds.includes(s.id) && s.type !== 'zonebox');
  if (!moving.length || !others.length) return;
  const TH = 4; // snap threshold (world units)
  moving.forEach((m) => {
    const mE: EdgeMap = { l: m.x, r: m.x + m.w, cx: m.x + m.w / 2, t: m.y, b: m.y + m.h, cy: m.y + m.h / 2 };
    others.forEach((o) => {
      const oE: EdgeMap = { l: o.x, r: o.x + o.w, cx: o.x + o.w / 2, t: o.y, b: o.y + o.h, cy: o.y + o.h / 2 };
      // Vertical alignment (compare left/right/centre-x).
      (['l', 'r', 'cx'] as const).forEach((mk) =>
        (['l', 'r', 'cx'] as const).forEach((ok) => {
          if (Math.abs(mE[mk] - oE[ok]) < TH) {
            S.smartGuides.push({
              type: 'v',
              x: oE[ok],
              y1: Math.min(m.y, o.y) - 10,
              y2: Math.max(m.y + m.h, o.y + o.h) + 10,
            });
          }
        }),
      );
      // Horizontal alignment (compare top/bottom/centre-y).
      (['t', 'b', 'cy'] as const).forEach((mk) =>
        (['t', 'b', 'cy'] as const).forEach((ok) => {
          if (Math.abs(mE[mk] - oE[ok]) < TH) {
            S.smartGuides.push({
              type: 'h',
              y: oE[ok],
              x1: Math.min(m.x, o.x) - 10,
              x2: Math.max(m.x + m.w, o.x + o.w) + 10,
            });
          }
        }),
      );
    });
  });
}
